import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import {
    Box,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
    CURRENCY,
    DEFAULT_VAT_PERCENTAGE,
    SIZE_TYPES,
    WEIGHT_OPTIONS
} from "../../constants/Variables";
import { baseUrl } from "../../services";
import { getAllAttributesWithValues } from "../../services/attributeFn";
import {
    calculateLossAmount,
    calculateProductFinalPrice,
    calculateVat,
    handleOpenSnackBar,
    roundTo2Decimal,
} from "../../utils";
import CustomDropDown from "../CutomDropDown";
import AddCustomVaraintValues from "../Dialog/AddCustomVaraintValues";
import DynamicMenuListComposition from "../DynamicMenuList";
import TextInput from "../TextInputs";



// const PREDEFINED_ATTRIBUTES = [
//     { label: "Color", value: "color" },
//     { label: "Size", value: "size" },
//     { label: "Weight", value: "weight" },
// ];

export default function ProductVariantManager({
    productPageType,
    attributes,
    setAttributes,
    variants,
    setVariants,
    vat,
    onUpdateVariantStatus,
}) {
    const { t } = useTranslation(["products", "common"]);
    const addCustomVaraintValuesRef = useRef(null);
    const { currentLanguage } = useSelector((state) => state.languageReducer);
    const [allAttributes, setAllAttributes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const userData = useSelector((state) => state.userData.user);

    // console.log(attributes, "ATTRIBUTES==============>");
    // console.log(variants, "VARIANTS==============>");

    const fetchAllAttributesWithValues = async () => {
        try {

            const response = await getAllAttributesWithValues();
            if (response.data.status === "success") {
                let data = response?.data.data.reverse();
                setAllAttributes(data);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllAttributesWithValues();
    }, []);

    console.log(allAttributes, "allAttributes");

    // ➕ Add attribute
    const handleAddAttribute = (value) => {
        if (attributes.some((a) => a.value === value)) return;
        const attr = allAttributes.find((a) => a.value === value);

        setAttributes((prev) => [
            ...prev,
            {
                // ...attr,
                label: attr.label,
                value: attr.value,
                attributeId: attr._id,
                values: [],
                // ...(value === "size" ? { sizeType: "" } : {}),
            },
        ]);
    };

    // ❌ Remove attribute
    const handleRemoveAttribute = (value) => {
        setAttributes((prev) => prev.filter((a) => a.value !== value));
    };

    // 🔁 Generate variants (preserve edits)
    useEffect(() => {
        const validAttrs = attributes.filter((a) => a.values.length);

        if (!validAttrs.length) {
            setVariants([]);
            return;
        }

        const combos = validAttrs.reduce(
            (acc, attr) =>
                acc.flatMap((item) =>
                    attr.values.map((val) => ({
                        ...item,
                        [attr.value]: val,
                    })),
                ),
            [{}],
        );

        setVariants((prev) =>
            combos.map((combo) => {
                const key = JSON.stringify(
                    Object.fromEntries(
                        Object.entries(combo).map(([k, v]) => [k, v.value]),
                    ),
                );

                const existing = prev.find(
                    (v) =>
                        JSON.stringify(
                            Object.fromEntries(
                                Object.entries(v.attributes).map(([k, v]) => [k, v.value]),
                            ),
                        ) === key,
                );

                return (
                    existing || {
                        attributes: combo,
                        sku: Object.values(combo)
                            .map((v) => v.value.toUpperCase().split(" ").join("-"))
                            .join("-"),
                        quantity: 0,
                        price: 0,
                        profitMargin: 0,
                        discount: 0,
                        finalPrice: 0,
                        status: true,
                        images: [],
                    }
                );
            }),
        );
    }, [attributes]);

    const handleChangeImage = (event, index) => {
        const file = event.target.files[0];

        const maxSize = 3 * 1024 * 1024;

        // Check file size
        if (file?.size > maxSize) {
            return handleOpenSnackBar("error", t("validation.imageSizeLimit"));
        }

        const imageType = file?.type.split("/")[1].toLowerCase();

        const allowedFormats = ["png", "jpeg", "jpg"];
        const allowedVideoFormats = ["mp4", "mov", "avi", "mkv"]; // Not using now

        // Check if the image format is valid
        if (!allowedFormats.includes(imageType)) {
            return handleOpenSnackBar("error", t("validation.allowedImageFormats"));
        }

        // If video file, show error
        if (allowedVideoFormats.includes(imageType)) {
            return handleOpenSnackBar("error", t("validation.videoNotAllowed"));
        }

        // Use FileReader to load the image and check its dimensions
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // Check if the image dimensions are at least 800x800
                if (img.width <= 999 || img.height <= 799) {
                    return handleOpenSnackBar("error", t("validation.minImageDimensions"));
                }

                // Proceed with the image upload
                const formData = new FormData();
                formData.append("image", file);

                axios({
                    method: "post",
                    url: `${baseUrl}image/upload`,
                    data: formData,
                    headers: { "Content-Type": "multipart/form-data" },
                })
                    .then((res) => {
                        handleOpenSnackBar("success", t("success.imageUploadSuccess"));
                        const copy = [...variants];
                        copy[index].images = [res?.data?.url];
                        setVariants(copy);
                    })
                    .catch((error) => {
                        handleOpenSnackBar("error", t("error.imageUploadFailed"));
                    });
            };

            img.src = e.target.result; // Load image as source
        };

        reader.readAsDataURL(file); // Read the file as data URL
    };

    const handleAddCustomVaraintValues = (type, payload) => {
        const selected = WEIGHT_OPTIONS.find(
            (v) => v.value === payload.value,
        );
        if (selected) return;
        if (attributes.find((v) => v.value === type)?.values.some((v) => v.value === payload.value))
            return;
        const copy = [...attributes];
        copy.find((v) => v.value === type)?.values.push(payload);
        setAttributes(copy);
    };

    return (
        <Stack mt={2}>

            {productPageType === "add" && (
                <>
                    <Box mb={1}>
                        <Typography fontSize={20} fontWeight={600}>
                            {t("variantOptions")}
                        </Typography>
                        <Typography fontSize={14} color={"text.secondary"}>
                            {t("variantOptionsDescription")}
                        </Typography>
                    </Box>

                    {/* STEP 1: ADD ATTRIBUTES */}

                    <DynamicMenuListComposition
                        buttonLabel="Add Options Like Size, Color, Weight"
                        buttonProps={{
                            variant: "add_attribute_button",
                            startIcon: <AddCircleOutlineIcon />,
                        }}
                        closeOnClick

                    >
                        {allAttributes?.length > 0 ? allAttributes.map((attr) => (
                            <MenuItem
                                key={attr._id}
                                disabled={attributes.some((a) => a.value === attr.value)}
                                onClick={() => handleAddAttribute(attr.value)}
                            >
                                {attr.label[currentLanguage]}
                            </MenuItem>
                        )) : (
                            <MenuItem disabled>
                                {t("noAttributesFound", { ns: "common" })}
                            </MenuItem>
                        )}
                    </DynamicMenuListComposition>

                    {allAttributes?.length === 0 && isLoading === false && (
                        <Stack
                            mt={2}
                            direction={"row"}
                            alignItems={"center"}
                            spacing={1}
                            px={1.2}
                            py={0.8}
                            borderRadius={1.5}
                            width={"100%"}
                            sx={{
                                backgroundColor: "#FFF4E5",
                                border: "1px solid #FFD8A8",
                            }}
                        >
                            <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: "#B54708" }} />
                            <Stack>
                                <Typography fontSize={16} fontWeight={500} color={"#8A4B08"}>
                                    {t("noAttributesFound", { ns: "common" })}
                                </Typography>
                                <Typography fontSize={13} color={"#8A4B08"}>
                                    {t("noAttributesFoundDescription", { ns: "common" })}
                                </Typography>
                            </Stack>
                        </Stack>
                    )}

                    {/* STEP 2: ATTRIBUTE VALUES */}
                    <Grid container spacing={2} mt={1} height="100%">
                        {attributes.map((attr, idx) => {
                            // const valueOptions =
                            //     attr.value === "color"
                            //         ? COLOR_OPTIONS
                            //         : attr.value === "weight"
                            //             ? WEIGHT_OPTIONS
                            //             : attr.sizeType
                            //                 ? SIZE_BY_TYPE[attr.sizeType] || []
                            //                 : [];

                            const finalDefinedValues = allAttributes.find((v) => v._id === attr.attributeId)?.values;


                            const valueOptions = finalDefinedValues;

                            return (
                                <Grid item xs={12} md={6} key={attr.value}>
                                    <Stack
                                        p={1}
                                        border="1px solid #eee"
                                        borderRadius={2}
                                        spacing={1}
                                        height="100%"
                                    >
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography fontWeight={600}>{attr.label[currentLanguage]} Values</Typography>
                                            {productPageType === "add" && (
                                                <IconButton onClick={() => handleRemoveAttribute(attr.value)}>
                                                    <DeleteOutlineIcon htmlColor="red" fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Stack>

                                        {/* SIZE TYPE RADIO */}
                                        {/* {attr.value === "size" && (
                                    <RadioGroup
                                        row
                                        value={attr.sizeType}
                                        onChange={(e) => {
                                            const copy = [...attributes];
                                            copy[idx].sizeType = e.target.value;
                                            copy[idx].values = [];
                                            setAttributes(copy);
                                        }}
                                    >
                                        {SIZE_TYPES.map((t) => (
                                            <FormControlLabel
                                                key={t.value}
                                                value={t.value}
                                                control={<Radio size="small" />}
                                                label={t.label} // Noticed
                                            />
                                        ))}
                                    </RadioGroup>
                                )} */}

                                        {/* VALUES DROPDOWN */}
                                        <CustomDropDown
                                            // icon={<FilterAltIcon />}
                                            // disabled={attr.value === "size" && !attr.sizeType}
                                            value=""
                                            // multiple
                                            onChange={(e) => {
                                                const selected = valueOptions.find(
                                                    (v) => v.value === e.target.value,
                                                );
                                                if (!selected) return;
                                                if (attr.values.some((v) => v.value === selected.value))
                                                    return;

                                                console.log(selected, "SELECTED");

                                                const copy = [...attributes];
                                                copy[idx].values.push({
                                                    label: selected.label,
                                                    value: selected.value,
                                                    attributeId: attr.attributeId,
                                                    attributeValueId: selected._id,
                                                });
                                                setAttributes(copy);
                                            }}
                                        >
                                            {valueOptions?.length > 0 ? (
                                                valueOptions.map((opt) => {
                                                    const isDisabled = attr.values.some((v) => v.value === opt.value);

                                                    return (
                                                        <MenuItem
                                                            key={opt.value}
                                                            value={opt.value}
                                                            disabled={isDisabled}
                                                        >
                                                            {opt.label[currentLanguage]}
                                                        </MenuItem>
                                                    );
                                                })
                                            ) : (
                                                <MenuItem disabled>
                                                    {t("noAttributeValuesFound", { ns: "common" })}
                                                </MenuItem>
                                            )}


                                        </CustomDropDown>

                                        {/* CHIPS */}
                                        <Stack direction="row" gap={1} flexWrap="wrap">
                                            {attr.values.map((v) => (
                                                <Chip
                                                    key={v.value}
                                                    label={v.label[currentLanguage]}

                                                    onDelete={productPageType === "add" && (() => {
                                                        const copy = [...attributes];
                                                        copy[idx].values = copy[idx].values.filter(
                                                            (x) => x.value !== v.value,
                                                        );
                                                        setAttributes(copy);
                                                    })}
                                                    variant="outlined"
                                                    sx={{ borderRadius: "6px" }}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                </Grid>
                            );
                        })}
                    </Grid>

                </>
            )}

            {/* STEP 3: VARIANTS TABLE */}

            {variants.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: productPageType === "add" ? 3 : 0 }}>
                    <Table size="small" sx={{ minWidth: "1500px" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("image")}</TableCell>
                                {attributes.map((attr) => (
                                    <TableCell key={attr.value}>
                                        {attr.label[currentLanguage]?.toUpperCase()}
                                    </TableCell>
                                ))}
                                <TableCell>{t("sku")}</TableCell>
                                <TableCell>{t("quantity")}</TableCell>
                                <TableCell>{t("price")}</TableCell>
                                <TableCell>{t("profitMargin")}</TableCell>
                                <TableCell>{t("discount")}</TableCell>
                                <TableCell>{t("finalPrice")}</TableCell>
                                <TableCell>{t("vatAmount", { vat: vat || DEFAULT_VAT_PERCENTAGE })}</TableCell>
                                <TableCell>{t("finalPriceWithVat")}</TableCell>
                                <TableCell
                                    sx={{
                                        position: "sticky",
                                        right: 0,
                                        zIndex: 3,
                                        px: 1,
                                        backgroundColor: "custom.white",
                                        boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",

                                    }}
                                >{t("status")}</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {variants.map((variant, index) => {
                                const finalProductPrice = calculateProductFinalPrice(
                                    variant.price,
                                    variant.discount,
                                    variant.profitMargin,
                                );

                                // This is calculated for both the product and the base packages
                                const productVat = calculateVat(
                                    finalProductPrice,
                                    vat || DEFAULT_VAT_PERCENTAGE,
                                );

                                // This is calculated for both the product and the base packages
                                const finalPriceAfterVat = roundTo2Decimal(
                                    finalProductPrice + productVat,
                                );

                                const lossAmount = calculateLossAmount(
                                    variant.price,
                                    variant.discount,
                                    variant.profitMargin,
                                );



                                return (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {variant.images.length > 0 ? (
                                                <img
                                                    src={variant.images[0]}
                                                    alt={variant.sku}
                                                    width={70}
                                                    height={70}
                                                    style={{ borderRadius: "6px", objectFit: "cover" }}
                                                />
                                            ) : (
                                                <Box
                                                    onClick={() =>
                                                        document.getElementById(`variant-image-${index}`).click()
                                                    }
                                                    sx={{
                                                        width: "70px",
                                                        height: "70px",
                                                        backgroundColor: "#f5f5f5",
                                                        border: "1px dashed #bbb",
                                                        borderRadius: "6px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor: "#eee",
                                                        },
                                                    }}
                                                >
                                                    <AddCircleOutlineIcon color="action" />
                                                </Box>
                                            )}

                                            {/* Hidden file input */}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                id={`variant-image-${index}`}
                                                hidden
                                                onChange={(e) => {
                                                    handleChangeImage(e, index);
                                                    // const file = e.target.files[0];
                                                    // if (!file) return;

                                                    // const imageUrl = URL.createObjectURL(file);

                                                    // const copy = [...variants];
                                                    // copy[index].images = [imageUrl]; // abhi first image
                                                    // setVariants(copy);
                                                }}
                                            />
                                        </TableCell>

                                        {attributes.map((attr) => (
                                            <TableCell key={attr.value}>
                                                {variant.attributes[attr.value]?.label[currentLanguage]}
                                            </TableCell>
                                        ))}

                                        <TableCell sx={{ width: "180px" }}>
                                            <TextInput
                                                value={variant.sku}
                                                onChange={(e) => {
                                                    const copy = [...variants];
                                                    copy[index].sku = e.target.value;
                                                    setVariants(copy);
                                                }}
                                                disabled={productPageType === "edit"}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ width: "150px" }}>
                                            <TextInput
                                                type="number"
                                                value={variant.quantity}
                                                disabled={productPageType === "edit"}
                                                onChange={(e) => {
                                                    const copy = [...variants];
                                                    copy[index].quantity = Number(e.target.valueAsNumber);
                                                    setVariants(copy);
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ width: "150px" }}>
                                            <TextInput
                                                type="number"
                                                value={variant.price}
                                                disabled={productPageType === "edit"}
                                                onChange={(e) => {
                                                    const copy = [...variants];
                                                    copy[index].price = Number(e.target.valueAsNumber);
                                                    setVariants(copy);
                                                }}
                                                InputEndIcon={CURRENCY}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ width: "150px" }}>
                                            <TextInput
                                                type="number"
                                                value={variant.profitMargin}
                                                disabled={productPageType === "edit"}
                                                onChange={(e) => {
                                                    const copy = [...variants];
                                                    copy[index].profitMargin = Number(
                                                        e.target.valueAsNumber,
                                                    );
                                                    setVariants(copy);
                                                }}
                                                InputEndIcon="%"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ width: "130px" }}>
                                            <TextInput
                                                type="number"
                                                value={variant.discount}
                                                disabled={productPageType === "edit"}
                                                onChange={(e) => {
                                                    const copy = [...variants];
                                                    copy[index].discount = Number(e.target.valueAsNumber);
                                                    setVariants(copy);
                                                }}
                                                InputEndIcon={CURRENCY}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" gap={0.4}>
                                                <Typography variant="body2">
                                                    {finalProductPrice} {CURRENCY}
                                                </Typography>
                                                {lossAmount < 0 && (
                                                    <Tooltip
                                                        title={t("validation.youAreLosing", {
                                                            amount: lossAmount,
                                                            currency: CURRENCY,
                                                        })}
                                                    >
                                                        <ErrorOutlineRoundedIcon
                                                            fontSize="small"
                                                            color="warning"
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {productVat}  {CURRENCY}
                                        </TableCell>
                                        <TableCell>
                                            {finalPriceAfterVat}   {CURRENCY}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                position: "sticky",
                                                right: 0,
                                                backgroundColor: "secondary.light",
                                                zIndex: 2,
                                                minWidth: 50,
                                                boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",

                                            }}
                                        >
                                            <Switch
                                                size="small"
                                                checked={variant.status}
                                                onChange={(e) => {
                                                    if (productPageType === "edit") {
                                                        onUpdateVariantStatus(variant._id, e)
                                                        const copy = [...variants];
                                                        copy[index].status = e.target.checked;
                                                        setVariants(copy);
                                                    } else {
                                                        const copy = [...variants];
                                                        copy[index].status = e.target.checked;
                                                        setVariants(copy);
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

            )}

            {productPageType === "edit" && (
                <Typography variant="body2" mt={2} color="text.secondary">
                    {t("note")}: {t("updatePricingAndQuantityInRestockSection")}
                </Typography>
            )}

            <AddCustomVaraintValues ref={addCustomVaraintValuesRef} onConfirm={handleAddCustomVaraintValues} />
        </Stack>
    );
}
